import JSZip, { JSZipObject } from "jszip";
import { FILE_CONTENT } from "../config";
import { LocalStorageType, ResourceType, SortByTypes, UserStorage, ZipFileItem } from "../types";
import imageCompression from "browser-image-compression";
import secureHelper from "./secure.helper";

const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
]

const days = ["Lunes", "Martes", "Miercoles", "Jueves", "Viernes", "Sabado", "Domingo"];

interface HandleReadUserStorage<T> {
    key: string,
    userStorage: UserStorage,
    encrypted?: boolean
}

interface HandleSaveUserStorage<T> extends HandleReadUserStorage<T> {
    value: T
}

function saveUserStorage<T>({key, value, userStorage, encrypted}: Readonly<HandleSaveUserStorage<T>>) {
    let stringData = JSON.stringify({ data: value })
    if (encrypted) {
        stringData = secureHelper.encriptApiRequest(stringData, key)
    }
    if (userStorage === 'local') {
        localStorage.setItem(key, stringData)
    } else {
        sessionStorage.setItem(key, stringData)
    }
}

function readUserStorage<T>({key, userStorage, encrypted}: Readonly<HandleReadUserStorage<T>>) {
    let stringData = userStorage === 'local' ? localStorage.getItem(key) : sessionStorage.getItem(key)
    if (!stringData) {
        return null
    }
    if(encrypted){
        stringData = secureHelper.decriptApiRequest(stringData, key)
    }
    try {
        let json = JSON.parse(stringData) as LocalStorageType<T>
        return json.data
    } catch (e) {
        return null
    }
}

const util = {
    encodeToBase64: (str: string) => window.btoa(encodeURIComponent(str)),
    setDateFormat: (date = new Date(), format = "dd/mm/yyyy") => {
        const currentMonth = date.getMonth();
        const currentHour = date.getHours();
        const keys = [
            { text: /yyyy|YYYY/g, value: date.getFullYear().toString() },
            { text: /yy|YY/g, value: String(date.getFullYear()).substring(0, 2) },
            { text: "MONTH", value: months[currentMonth].toUpperCase() },
            { text: "month", value: months[currentMonth] },
            {
                text: "MMM",
                value: months[currentMonth].toUpperCase().substring(0, 3),
            },
            { text: "mmm", value: months[currentMonth].substring(0, 3) },
            { text: /mm|MM/g, value: String(currentMonth + 1).padStart(2, "0") },
            { text: "DAY", value: days[date.getDay()] },
            { text: /dd|DD/g, value: String(date.getDate()).padStart(2, "0") },
            { text: "HH24", value: String(currentHour).padStart(2, "0") },
            {
                text: "HH",
                value: String(
                    currentHour > 12 ? currentHour - 12 : currentHour
                ).padStart(2, "0"),
            },
            {
                text: "AMPM",
                value: String(currentHour > 12 ? "PM" : "AM").padStart(2, "0"),
            },
            {
                text: "ampm",
                value: String(currentHour > 12 ? "pm" : "am").padStart(2, "0"),
            },
            { text: "MI", value: String(date.getMinutes()).padStart(2, "0") },
            { text: "SS", value: String(date.getSeconds()).padStart(2, "0") },
        ];
        keys.forEach((key) => {
            format = format.replaceAll(key.text, key.value);
        });
        return format;
    },
    setFormatDate: (dateString: string, divider = '/') => {
        try {
            let parts = dateString.split(divider);
            let date = new Date(parseInt(parts[2], 10),
                parseInt(parts[1], 10) - 1,
                parseInt(parts[0], 10));
            return date
        } catch (e) {
            return null
        }
    },
    saveUserStorage,
    readUserStorage,
    detectOS: () => {
        let userAgent = window.navigator.userAgent,
            platform = window.navigator.platform,
            macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
            windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'],
            iosPlatforms = ['iPhone', 'iPad', 'iPod'],
            os = "Undefined";

        if (macosPlatforms.indexOf(platform) !== -1) {
            os = 'Mac OS';
        } else if (iosPlatforms.indexOf(platform) !== -1) {
            os = 'iOS';
        } else if (windowsPlatforms.indexOf(platform) !== -1) {
            os = 'Windows';
        } else if (/Android/.test(userAgent)) {
            os = 'Android';
        } else if (!os && /Linux/.test(platform)) {
            os = 'Linux';
        }

        return os;
    },
    downloadFile: (fileName: string, blob?: Blob | null) => {
        if (blob) {
            let link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    },
    validateToEncrypt: (originalString: string) => {
        try {
            return JSON.stringify(JSON.parse(originalString))
        } catch (e) {
            return originalString
        }
    },
    readZipFile: async function (zip: JSZip, filename: string, onRead: (data?: ZipFileItem) => void) {
        const mapFiles = new Map<string, ZipFileItem>()
        for (let key in zip.files) {
            let originalFile = zip.files[key]
            let item = mapZipFileTreeItem(originalFile)
            if (!mapFiles.get(item.path)) {
                let root = {
                    name: filename + (item.path ? '/' + item.path : ''),
                    path: '/',
                    isDirectory: true,
                    date: new Date(),
                    origin: item.path,
                    files: []
                }
                mapFiles.set(item.path, root)
            }
            if (item.isDirectory) {
                item.files = []
                mapFiles.set(`${item.path ? item.path + '/' : ''}${item.name}`, item)
                mapFiles.get(item.path)?.files?.unshift(item)
            } else {
                let data = await zip.file(item.origin)?.async("blob")
                item.content = data
                mapFiles.get(item.path || '/')?.files?.push(item)
            }
        }
        onRead(mapFiles.values().next().value)
    },
    processImageFileSize,
    getFileExtension,
    getNavigatorData: () => {
        let ua = navigator.userAgent;
        let browserMatch = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        let tem = ua.match(/version\/(\d+)/i);
        browserMatch = [browserMatch[1], browserMatch[2]];
        if (tem) {
            browserMatch.splice(1, 1, tem[1]);
        }
        if (browserMatch[0] === 'Chrome') {
            if (ua.match(/\bEdg\/(\d+)/)) {
                return { name: 'Edge', version: browserMatch[1] };
            }
            if (ua.match(/\bOPR|Edge\/(\d+)/)) {
                return { name: 'Opera', version: browserMatch[1] };
            }
        }
        return {
            name: browserMatch[0],
            version: browserMatch[1],
            mobile: validateIsMobile()
        };
    },
    validateError: (e: unknown): string => {
        let message = typeof e === 'string' ? e : 'Ocurrio un error'
        if (e instanceof Error) {
            message = e.message
        }
        return message
    },
    restoreNexPageUrl: (data: URLSearchParams, urlDefault: string): string => {
        let url = urlDefault
        let params: string[] = []
        data.forEach((value, key) => {
            if (key === 'next') {
                url = value
            } else {
                params.push(`${key}=${value}`)
            }
        })
        if (params.length > 0) {
            url += `?${params.join('&')}`
        }
        return url
    },
    sortResourceItems: (option: SortByTypes, desc: boolean) => (a: ResourceType, b: ResourceType) => {
        let result = 0
        switch (option) {
            case 'name':
                result = a.label.localeCompare(b.label)
                break;
            case 'updatedAt':
                result = new Date(a.updatedAt || 0) > new Date(b.updatedAt || 0) ? -1 : 1
                break;
            case 'createdAt':
                result = new Date(a.updatedAt || 0) > new Date(b.updatedAt || 0) ? -1 : 1
                break;
            case 'fileType':
                result = getFileExtension(a.versions[0]?.artifacts[0].name).extension.localeCompare(getFileExtension(b.versions[0]?.artifacts[0].name).extension)
                break;
            default:
                break;
        }
        return result * (desc ? 1 : -1)
    },
    reduceString
}

function reduceString(data: string, length: number) {
    if (data.length > length) {
        return data.substring(0, length - 3) + '...';
    }
    return data;
}

function getFileExtension(fileName: string = '') {
    const filesContainers = [
        { container: 'image', types: FILE_CONTENT.img },
        { container: 'video', types: FILE_CONTENT.video },
        { container: 'text', types: FILE_CONTENT.text },
        { container: 'xml', types: FILE_CONTENT.xml },
        { container: 'excel', types: FILE_CONTENT.excel },
        { container: 'zip', types: ['zip'] },
        { container: 'application', types: FILE_CONTENT.application }
    ]
    let filePart = fileName.split('.')
    let fileExtension = filePart[filePart.length - 1].toLowerCase()
    let mime = filesContainers.find(m => m.types.includes(fileExtension))
    return { extension: fileExtension, type: mime?.container || 'other' }
}


function mapZipFileTreeItem(originalFile: JSZipObject): ZipFileItem {
    let path = originalFile.name.split('/')
    if (originalFile.dir) {
        path.pop()
    }
    return {
        name: path.pop() || '',
        path: path.join('/'),
        isDirectory: originalFile.dir,
        date: originalFile.date,
        origin: originalFile.name
    }
}

async function processImageFileSize(inputFile: File, maxSizeMB = 1, additionalOptions = {}): Promise<File> {
    const mbVal = 1048576;
    let currentSize = inputFile.size / mbVal
    if (currentSize > maxSizeMB) {
        let options = {
            maxSizeMB: maxSizeMB,
            ...additionalOptions
        };
        const output = await imageCompression(inputFile, options);
        //images greater than 12mb
        if (output.size > maxSizeMB) {
            return processImageFileSize(output, maxSizeMB)
        }
        return output
    }
    return Promise.resolve(inputFile)
}

export const validateIsMobile = () => {
    let ua = navigator.userAgent;
    let matchMobile = ua.match(/Android|BlackBerry|Opera Mini|IEMobile|iPhone|iPad|iPod/i) || [];
    return matchMobile.length > 0
}

export function byteConverter( bytes: number, decimals: number, only?: string) {
    const K_UNIT = 1024;
    const SIZES = ["Bytes", "KB", "MB", "GB", "TB", "PB"];

    if(bytes== 0) return "0 Byte";
  
    if(only==="MB") return (bytes / (K_UNIT*K_UNIT)).toFixed(decimals) + " MB" ;
  
    let i = Math.floor(Math.log(bytes) / Math.log(K_UNIT));
    let resp = parseFloat((bytes / Math.pow(K_UNIT, i)).toFixed(decimals)) + " " + SIZES[i];
  
    return resp;
}

export default util